// Shared scaffolding for the web parser modules.
//
// Browser-friendly port of src/main/parsers/shared.js — the algorithms
// are identical, only the runtime swap is `crypto.randomUUID` instead
// of `crypto.randomBytes`. Every web parser (fountain.js, fdx.js,
// pdf.js) emits the same `{ title, titlePage, prelude, scenes,
// characters }` shape so the bridge can hand the result straight to
// `buildScript`.

export const BLOCK_TYPES = Object.freeze([
  'scene-heading',
  'action',
  'character',
  'parenthetical',
  'dialogue',
  'transition',
  'shot',
  'centered',
]);

export const FDX_TYPE_MAP = Object.freeze({
  'Scene Heading': 'scene-heading',
  Action: 'action',
  Character: 'character',
  Dialogue: 'dialogue',
  Parenthetical: 'parenthetical',
  Transition: 'transition',
  Shot: 'action',
  General: 'action',
});

export function uuid() {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

// Full UUID v4 — used for `Lastslate-ID:` headers, cloud bindings,
// and anywhere the canonical UUID format is required (matches
// src/main/fountain/uuid-header.js's contract).
export function uuidv4() {
  return crypto.randomUUID();
}

const LASTSLATE_ID_RE =
  /^lastslate[\s-]*id\s*:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/im;

// Extract the Lastslate-ID UUID from a fountain title block. Returns
// null if absent. The Electron app uses this as canonical doc
// identity so the same script always opens against the same Y.Doc
// regardless of file moves on disk.
export function extractLastslateId(fountainText) {
  if (typeof fountainText !== 'string') return null;
  const m = LASTSLATE_ID_RE.exec(fountainText);
  return m ? m[1].toLowerCase() : null;
}

// Inject (or replace) a Lastslate-ID in a fountain title block.
// Idempotent: returns the original text if the header is already
// present. Otherwise prepends a new line at the very top so it
// survives subsequent title-page edits.
export function ensureLastslateId(fountainText, id) {
  if (extractLastslateId(fountainText)) return fountainText;
  const headerLine = `Lastslate-ID: ${id || uuidv4()}\n`;
  return headerLine + (fountainText || '');
}

export function titleFromHeading(heading) {
  if (!heading) return 'Untitled Scene';
  return (
    heading
      .replace(/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)\s*/i, '')
      .replace(/\s*-\s*(DAY|NIGHT|MORNING|EVENING|DUSK|DAWN|CONTINUOUS|LATER).*$/i, '')
      .trim() || heading
  );
}

export function newScene(number, heading) {
  return {
    id: uuid(),
    number,
    heading: heading || '',
    title: titleFromHeading(heading),
    blocks: [],
    characters: [],
  };
}

export function newBlock(type, text) {
  if (!BLOCK_TYPES.includes(type)) {
    throw new Error(`Unknown block type: ${type}`);
  }
  return { id: uuid(), type, text };
}

export function characterKey(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .replace(/\s*\(.*\)\s*$/, '')
    .trim()
    .toUpperCase();
}

export function recordCharacter(characterMap, name, scene) {
  const key = characterKey(name);
  if (!key) return;
  if (!characterMap[key]) {
    characterMap[key] = { name: key, lines: [], scenes: [] };
  }
  if (scene && !characterMap[key].scenes.includes(scene.id)) {
    characterMap[key].scenes.push(scene.id);
  }
  if (scene && !scene.characters.includes(key)) {
    scene.characters.push(key);
  }
}

export function recordDialogue(characterMap, lastCharacter, text) {
  const key = characterKey(lastCharacter);
  if (!key || !characterMap[key]) return false;
  characterMap[key].lines.push(text);
  return true;
}

// Heuristics used by the PDF parser to promote action-classified
// blocks to scene headings / transitions.
export function isSceneHeading(line) {
  if (!line) return false;
  if (line.startsWith('.') && !line.startsWith('..')) return true;
  return /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)\s+/i.test(line);
}
